import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiClock, FiCheckCircle, FiArrowRight, FiTool, FiShield } from 'react-icons/fi';
import RelatedInventory from '../../../components/RelatedInventory';

const EuropeanCarRepair = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const europeanBrands = [
    {
      name: 'BMW',
      title: 'Dallas BMW Repair',
      description: 'Specializing in N54/N55 engines, electrical diagnostics, and performance tuning.',
      url: '/services/european-car-repair/bmw',
      image: '/assets/assets/images/new/euro-repair-img/bmw-valve-cover-gasket.webp'
    },
    {
      name: 'Porsche',
      title: 'Porsche Repair Dallas & Fort Worth',
      description: 'IMS bearing experts, PDK transmission service, PIWIS diagnostics. Serving Dallas & Fort Worth.',
      url: '/services/european-car-repair/porsche',
      image: '/assets/assets/images/new/euro-repair-img/porsche-euro-hd.webp'
    },
    {
      name: 'Audi',
      title: 'Dallas Audi Maintenance & Repair',
      description: 'Electrical systems, DSG transmission service, and turbocharger diagnostics.',
      url: '/repair/european-vehicles/audi',
      image: '/assets/assets/images/new/euro-repair-img/Audi-repair.webp'
    },
    {
      name: 'Jaguar',
      title: 'Jaguar Services & Repair',
      description: 'Electrical diagnostics, suspension repairs, and InControl system specialists.',
      url: '/repair/european-vehicles/jaguar',
      image: '/assets/assets/images/jag.webp'
    },
    {
      name: 'Land Rover',
      title: 'Land-Rover & Range-Rover Repair',
      description: 'Air suspension systems, electrical diagnostics, and off-road performance.',
      url: '/repair/european-vehicles/land-rover',
      image: '/assets/assets/images/new/euro-repair-img/rangerover-euro.webp'
    },
    {
      name: 'Mini Cooper',
      title: 'North-Texas Mini-Cooper Repair',
      description: 'Specialized service for all Mini models including performance tuning.',
      url: '/repair/european-vehicles/mini-cooper',
      image: '/assets/assets/images/new/euro-repair-img/mini-cooper.webp'
    },
    {
      name: 'Volkswagen',
      title: 'Volkswagen Repair',
      description: 'Complete VW service from Beetles to Atlas, specializing in TDI diesels.',
      url: '/repair/european-vehicles/volkswagen',
      image: '/assets/assets/images/vw-repair.webp'
    },
    {
      name: 'Volvo',
      title: 'Dallas Volvo Repair',
      description: 'Safety system specialists with expertise in all Volvo models.',
      url: '/repair/european-vehicles/volvo',
      image: '/assets/assets/images/volvo-repair.webp'
    },
    {
      name: 'Mercedes-Benz',
      title: 'Mercedes Service & Repair in DFW',
      description: 'AMG servicing, maintenance & repairs + performance upgrades!',
      url: '/repair/european-vehicles/mercedes',
      image: '/assets/assets/images/new/euro-repair-img/mercedes.webp'
    }
  ];

  const dallasNeighborhoods = [
    {
      area: 'Highland Park European Auto Repair',
      title: 'Advanced BMW/Mercedes Diagnostics',
      description: 'European auto repair Dallas specialists serving Highland Park:',
      services: [
        'BMW Valvetronic system diagnostics and repair',
        'Mercedes electronic accessory troubleshooting',
        'Performance tuning for European luxury sedans'
      ]
    },
    {
      area: 'Uptown Dallas European Auto Repair',
      title: 'Porsche/Audi Specialist Services',
      description: 'Our European auto repair Dallas experts address premium vehicle issues:',
      services: [
        'Porsche PDK transmission diagnostic and repair',
        'Audi MMI system troubleshooting and updates',
        'Performance optimization for European sports cars'
      ]
    },
    {
      area: 'Preston Hollow European Car Service',
      title: 'Luxury European Vehicle Specialists',
      description: 'European auto repair Dallas services for Preston Hollow residents:',
      services: [
        'Range Rover air suspension repair',
        'Bentley and Rolls-Royce maintenance',
        'European luxury SUV specialists'
      ]
    },
    {
      area: 'North Dallas European Auto Repair',
      title: 'Full-Service European Diagnostics',
      description: 'Comprehensive European auto repair Dallas coverage for North Dallas:',
      services: [
        'Volvo safety system diagnostics',
        'VW/Audi DSG transmission service',
        'European diesel specialists'
      ]
    },
    {
      area: 'Plano & Richardson European Repair',
      title: 'European Car Service Near Plano',
      description: 'European auto repair serving Plano and Richardson drivers:',
      services: [
        'BMW cooling system repairs',
        'Mercedes AIRMATIC suspension service',
        'Porsche brake system specialists'
      ]
    },
    {
      area: 'Frisco & McKinney European Service',
      title: 'European Auto Repair North of Dallas',
      description: 'European car specialists serving Frisco and McKinney:',
      services: [
        'Audi quattro system diagnostics',
        'Land Rover terrain response repair',
        'MINI Cooper turbo specialists'
      ]
    }
  ];

  const serviceProcess = [
    {
      step: 1,
      title: 'Same-Day Diagnostics',
      description: 'Most European auto repair diagnostics completed by 3PM for Dallas clients, allowing for quick decision-making on your vehicle\'s needs.'
    },
    {
      step: 2,
      title: 'Transparent Approval Process',
      description: 'Detailed repair recommendations with photos/videos of identified issues sent directly to you, ensuring complete understanding before work begins.'
    },
    {
      step: 3,
      title: 'Efficient Completion',
      description: 'Our European auto repair specialists work efficiently to return your vehicle promptly, with thorough quality checks before delivery.'
    }
  ];

  const faqItems = [
    {
      question: 'How far is your shop from Dallas?',
      answer: 'Our Paradise, TX location is a short drive from Dallas. Many of our customers tell us the quick trip is well worth the superior service and savings they receive on their European vehicles. Check our Dallas service area for detailed directions.'
    },
    {
      question: 'How often should I service my European car in the Dallas area?',
      answer: 'Most European manufacturers recommend service intervals every 5000 miles or annually, whichever comes first. However, the Dallas-Fort Worth area\'s hot climate and stop-and-go traffic conditions may necessitate more frequent maintenance. We recommend following your vehicle\'s specific factory scheduled maintenance, which our technicians can provide.'
    },
    {
      question: 'Will using your independent European car repair shop void my warranty?',
      answer: 'No. Under the Magnuson-Moss Warranty Act, manufacturers cannot void your warranty simply because you chose an independent repair shop. As long as the work is performed according to manufacturer specifications (which our shop follows), your warranty remains intact.'
    },
    {
      question: 'Do you use genuine European parts for repairs?',
      answer: 'Yes, we use genuine or OEM-quality parts for all our European car repairs. This ensures your vehicle maintains its performance, reliability, and value.'
    },
    {
      question: 'How do Dallas-Fort Worth driving conditions affect my European vehicle?',
      answer: 'The Dallas area\'s hot summers, occasional freezing winters, and heavy traffic can accelerate wear on cooling systems, air conditioning, brakes, and suspension components in European vehicles. Our preventative maintenance programs account for these local conditions.'
    }
  ];

  const technicalFAQs = [
    {
      question: 'What makes European vehicle diagnostics different from standard vehicle diagnostics?',
      answer: 'European vehicles utilize sophisticated integrated systems that require specialized diagnostic approaches including proprietary protocols, multi-layered diagnostic trees, model-specific pathways, coded fault memory, and adaptive learning parameters. At Paradise-Performance, our specialized diagnostic equipment includes factory-level scanners for BMW, Mercedes, Audi, Porsche and other European brands.',
      bulletPoints: [
        'Proprietary protocols that require manufacturer-specific diagnostic interfaces',
        'Multi-layered diagnostic trees that account for interconnected systems',
        'Model-specific diagnostic pathways that differ even within the same manufacturer',
        'Coded fault memory that requires interpretation beyond basic code reading',
        'Adaptive learning parameters that must be properly reset during repairs'
      ]
    },
    {
      question: 'How do turbocharger systems in European vehicles differ from domestic models?',
      answer: 'European turbocharger systems employ several advanced technologies that distinguish them from typical domestic implementations. Our technicians have extensive experience with European turbocharger systems and the advanced diagnostic equipment necessary to properly service them.',
      bulletPoints: [
        'Twin-scroll technology that separates exhaust pulses for improved efficiency',
        'Electronically actuated wastegates with precise control parameters',
        'Variable geometry turbochargers that adjust to different engine speeds',
        'Water-cooled center sections for improved durability and heat management',
        'Integrated intercooler systems with electronic monitoring'
      ]
    },
    {
      question: 'Why is carbon buildup a common issue in direct injection European engines?',
      answer: 'Direct injection technology in European engines creates a specific environment where carbon deposits can accumulate. Our shop employs specialized carbon cleaning methods including walnut shell blasting, chemical treatments, and intake service procedures developed specifically for European direct injection engines.',
      bulletPoints: [
        'Fuel injectors spray directly into combustion chambers rather than onto intake valves',
        'Intake valves don\'t receive the cleaning effect of fuel washing over them',
        'Oil vapor from the PCV system can deposit on intake valves and ports',
        'High-performance European engines often run at higher temperatures that can bake on deposits',
        'Complex intake geometries in European engines can accelerate deposit accumulation'
      ]
    },
    {
      question: 'What specialized equipment is required for European brake system service?',
      answer: 'Modern European brake systems feature technology that requires specialized service equipment. Our shop invests in specialized brake service equipment for each European brand we service.',
      bulletPoints: [
        'Electronic parking brake tools to properly retract calibers during service',
        'Pressure bleeding equipment for ABS and stability control integrated systems',
        'Brake control module scanners for proper coding and adaptation',
        'Special pad wear sensor tools for replacing electronic wear indicators',
        'Brake fluid analysis equipment to test hygroscopic fluid conditions'
      ]
    }
  ];

  const vehicleSpecificFAQs = [
    {
      question: 'How do adaptive suspension systems in European cars function?',
      answer: 'European adaptive suspension systems represent some of the most sophisticated technology in modern vehicles. Our technicians have specialized training in European adaptive suspension systems, with experience in diagnosing and repairing air suspension leaks, electronic damper failures, level sensor issues, and control module programming.',
      bulletPoints: [
        'Electronically controlled dampers adjust stiffness within milliseconds',
        'Height-adjustable air springs in systems like Mercedes Airmatic and Audi air suspension',
        'Dynamic body control that adjusts to driving conditions and driver inputs',
        'Multiple suspension modes (Comfort, Sport, Sport+) with different characteristic maps',
        'Integrated chassis control systems that communicate with stability control and all-wheel drive'
      ]
    },
    {
      question: 'What makes BMW cooling systems unique, and how do we service them?',
      answer: 'BMW cooling systems employ several design elements that require specialized knowledge. Our BMW cooling system service includes comprehensive pressure testing, electronic diagnostics, and proper bleeding procedures using specialized equipment.',
      bulletPoints: [
        'Electric water pumps that operate independently of engine speed',
        'Integrated thermostat/water pump housings in many models',
        'Complex bleeding procedures required for proper cooling system function',
        'Multiple temperature sensors throughout the cooling circuit',
        'Specialized coolant formulations with specific additives'
      ]
    },
    {
      question: 'How do we diagnose and repair Mercedes-Benz electrical system issues?',
      answer: 'Mercedes-Benz vehicles feature highly integrated electrical architectures that require specialized diagnostic approaches. Our Mercedes electrical diagnosis involves comprehensive network scanning, targeted module testing, and circuit analysis using factory-level diagnostic equipment.',
      bulletPoints: [
        'Multiple CAN-bus networks with different communication protocols',
        'Intelligent battery management systems that require registration procedures',
        'Control module gateways that manage communication between systems',
        'Sleep/wake mode issues that can cause parasitic drain',
        'Complex convenience systems with numerous interconnected modules'
      ]
    },
    {
      question: 'What specialized procedures are required for Audi/Volkswagen DSG transmission service?',
      answer: 'Dual-clutch transmissions in Audi and Volkswagen vehicles require specific service procedures. We perform complete DSG service with the proper equipment to maintain fill levels within the narrow temperature-dependent specifications.',
      bulletPoints: [
        'Specialized DSG fluid with exact specifications that differ from conventional ATF',
        'Temperature-controlled filling procedures requiring specific equipment',
        'Mechatronic unit adaptation after service or repair',
        'Clutch engagement point learning procedures using diagnostic equipment',
        'Filter replacement requiring specialized tools for proper access'
      ]
    }
  ];

  const performanceFAQs = [
    {
      question: 'What performance upgrades provide the best value for European vehicles?',
      answer: 'Strategic performance enhancements can significantly improve European vehicle dynamics without compromising reliability. Our approach to European performance upgrades focuses on balanced modifications that enhance drivability while maintaining reliability.',
      bulletPoints: [
        'ECU optimization that properly calibrates fuel, timing, and boost parameters',
        'Performance suspension components with proper spring and damper matching',
        'Upgraded brake components that maintain proper bias and ABS function',
        'Enhanced cooling solutions that support increased performance demands',
        'Intake and exhaust improvements designed specifically for European engine characteristics'
      ]
    }
  ];

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      <Helmet>
        <title>European Auto Repair Dallas | Expert European Car Service Dallas TX</title>
        <meta name="description" content="European auto repair in Dallas that doesn't cost a fortune. We work on BMWs, Mercedes, Porsches, Audis—all the good stuff. Real mechanics who know these cars inside and out. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair" />
        <meta property="og:title" content="European Auto Repair Dallas | BMW, Mercedes, Porsche Specialists" />
        <meta property="og:description" content="Dallas European auto repair experts. BMW, Mercedes, Porsche, Audi, VW specialists. 30-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/european-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="European Auto Repair Dallas | BMW, Mercedes, Porsche Specialists" />
        <meta name="twitter:description" content="Dallas European auto repair experts. BMW, Mercedes, Porsche, Audi, VW specialists. 30-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/european-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "European Auto Repair Dallas - Paradise-Performance",
            "description": "European auto repair Dallas specialists. Expert service for BMW, Mercedes, Porsche, Audi, Land Rover, Jaguar, Volvo, MINI. Located near Dallas, serving entire DFW metroplex.",
            "url": "https://paradise-performance.com/services/european-car-repair",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Highland Park", "University Park", "North Dallas", "Richardson", "Plano", "Frisco", "McKinney", "Allen", "Uptown Dallas", "Oak Lawn", "Preston Hollow", "Lakewood", "Deep Ellum", "Addison", "Carrollton", "The Colony", "Lewisville", "Flower Mound"],
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": 33.1501,
                "longitude": -97.7893
              },
              "geoRadius": "50000"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "European Auto Repair Services",
              "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "BMW Repair Dallas" }},
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mercedes-Benz Repair Dallas" }},
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Porsche Service Dallas" }},
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audi Repair Dallas" }},
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Land Rover Service Dallas" }},
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Jaguar Repair Dallas" }}
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "127"
            },
            "priceRange": "$$$",
            "openingHours": "Mo-Fr 09:00-17:00,Sa 10:00-14:00"
          })}
        </script>
        
        {/* FAQ Schema for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is your European auto repair shop from Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Paradise, TX location is about 30-40 minutes from Dallas. Many customers tell us the drive is worth it for the superior European auto repair service and 30-40% savings compared to Dallas dealerships."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my European car in Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most European manufacturers recommend service every 5,000-10,000 miles. However, Dallas's hot climate and stop-and-go traffic may require more frequent maintenance. We recommend oil changes every 5,000-7,500 miles for European vehicles in the Dallas area."
                }
              },
              {
                "@type": "Question",
                "name": "Will using an independent European auto repair shop void my warranty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. Under the Magnuson-Moss Warranty Act, manufacturers cannot void your warranty for using an independent repair shop. As long as work is performed to manufacturer specifications, which we follow, your warranty remains intact."
                }
              },
              {
                "@type": "Question",
                "name": "What European car brands do you service in Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We service all major European brands including BMW, Mercedes-Benz, Porsche, Audi, Volkswagen, Land Rover, Range Rover, Jaguar, Volvo, and MINI Cooper. Our technicians have factory-level diagnostic equipment for each brand."
                }
              },
              {
                "@type": "Question",
                "name": "How much can I save on European auto repair compared to Dallas dealerships?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dallas drivers typically save 30-40% on European auto repair at Paradise-Performance compared to dealership prices. We use the same OEM parts and diagnostic equipment but don't have the overhead costs of a fancy dealership showroom."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 pt-32 bg-gradient-to-br from-gray-400 to-gray-100 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              European Auto Repair Dallas
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl mb-8"
            >
              We fix European cars. BMWs, Mercedes, Porsches, Audis, Land Rovers, Jaguars—you name it. 
              Been doing this for years, and we're pretty good at it. Way cheaper than the dealerships, too.
            </motion.p>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="tel:8173822316"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold text-lg transition-colors"
              >
                <FiPhone className="mr-2" />
                (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Service
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {europeanBrands.map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={brand.url}
                  className="block bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group"
                >
                  <div className="h-48 relative overflow-hidden">
                    {brand.image ? (
                      <>
                        <img 
                          src={brand.image} 
                          alt={`${brand.name} Repair Service`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <span className="absolute bottom-4 left-4 text-white text-3xl font-bold drop-shadow-lg">{brand.name}</span>

      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="European Vehicles For Sale" />
      </div>
                      </>
                    ) : (
                      <div className="h-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center">
                        <span className="text-white text-4xl font-bold">{brand.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">
                      {brand.title}
                    </h2>
                    <p className="text-gray-600 mb-4">{brand.description}</p>
                    <span className="text-primary-600 font-semibold group-hover:underline">
                      {brand.name === 'Porsche' && 'Porsche Repair, Services & Maintenance →'}
                      {brand.name === 'Audi' && 'All Audi Services →'}
                      {brand.name === 'Jaguar' && 'Jaguar Services →'}
                      {brand.name === 'Land Rover' && 'View Land-Rover & Range-Rover Repair Services →'}
                      {brand.name === 'Mini Cooper' && 'Mini-Cooper Maintenance →'}
                      {brand.name === 'Volkswagen' && 'Volkswagen Vehicle Services →'}
                      {brand.name === 'Volvo' && 'Volvo Maintenance, Services & Repair →'}
                      {brand.name === 'Mercedes-Benz' && 'View Mercedes Services →'}
                      {brand.name === 'BMW' && 'View BMW Services →'}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhood Diagnostics */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            European Car Diagnostics for Dallas Neighborhoods
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Our European auto repair specialists provide tailored diagnostic solutions for specific neighborhood needs across Dallas:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {dallasNeighborhoods.map((neighborhood, index) => (
              <motion.div
                key={index}
                initial={{ x: index % 2 === 0 ? -30 : 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4">
                  <h3 className="text-xl font-semibold">{neighborhood.area}</h3>
                </div>
                <div className="p-6">
                  <h4 className="font-bold text-lg mb-2 text-primary-600">{neighborhood.title}</h4>
                  <p className="text-gray-600 mb-4">{neighborhood.description}</p>
                  <ul className="space-y-2">
                    {neighborhood.services.map((service, idx) => (
                      <li key={idx} className="flex items-start">
                        <FiCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            Your Dallas European Auto Repair Experience
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Our streamlined process ensures your European vehicle receives prompt, transparent service:
          </p>
          
          <div className="max-w-4xl mx-auto">
            {serviceProcess.map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="flex mb-8"
              >
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4 flex-shrink-0">
                  {process.step}
                </div>
                <div className="bg-white rounded-lg shadow-md p-6 flex-1 border-l-4 border-primary-400">
                  <h3 className="font-bold text-lg mb-2">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            Expert Answers to Your European Auto Repair Questions
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Find specialized information about advanced European vehicle maintenance, diagnostics, and repairs from our Paradise-Performance technicians serving customers throughout North Texas.
          </p>
          
          {/* General FAQs */}
          <div className="max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl font-bold mb-6">FAQ About European Car Repair for Dallas Drivers</h3>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(`general-${index}`)}
                    className="w-full px-6 py-4 text-left font-semibold flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span>{item.question}</span>
                    <span className="text-2xl">{expandedFAQ === `general-${index}` ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === `general-${index}` && (
                    <div className="px-6 pb-4 text-gray-700 border-t">
                      <p className="pt-4">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Technical FAQs */}
          <div className="max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl font-bold mb-6">Technical Diagnosis and Performance Solutions</h3>
            <div className="space-y-4">
              {technicalFAQs.map((item, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(`tech-${index}`)}
                    className="w-full px-6 py-4 text-left font-semibold flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span>{item.question}</span>
                    <span className="text-2xl">{expandedFAQ === `tech-${index}` ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === `tech-${index}` && (
                    <div className="px-6 pb-4 text-gray-700 border-t">
                      <p className="pt-4">{item.answer}</p>
                      {item.bulletPoints && (
                        <ul className="mt-4 space-y-2">
                          {item.bulletPoints.map((point, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary-600 font-bold mr-2">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Vehicle-Specific FAQs */}
          <div className="max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl font-bold mb-6">Vehicle-Specific Engineering Solutions</h3>
            <div className="space-y-4">
              {vehicleSpecificFAQs.map((item, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(`vehicle-${index}`)}
                    className="w-full px-6 py-4 text-left font-semibold flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span>{item.question}</span>
                    <span className="text-2xl">{expandedFAQ === `vehicle-${index}` ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === `vehicle-${index}` && (
                    <div className="px-6 pb-4 text-gray-700 border-t">
                      <p className="pt-4">{item.answer}</p>
                      {item.bulletPoints && (
                        <ul className="mt-4 space-y-2">
                          {item.bulletPoints.map((point, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary-600 font-bold mr-2">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Performance FAQs */}
          <div className="max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl font-bold mb-6">Performance Enhancement and Modification Solutions</h3>
            <div className="space-y-4">
              {performanceFAQs.map((item, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(`performance-${index}`)}
                    className="w-full px-6 py-4 text-left font-semibold flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span>{item.question}</span>
                    <span className="text-2xl">{expandedFAQ === `performance-${index}` ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === `performance-${index}` && (
                    <div className="px-6 pb-4 text-gray-700 border-t">
                      <p className="pt-4">{item.answer}</p>
                      {item.bulletPoints && (
                        <ul className="mt-4 space-y-2">
                          {item.bulletPoints.map((point, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary-600 font-bold mr-2">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Technical Expertise Note */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 max-w-3xl mx-auto">
            <p className="text-gray-700">
              <strong className="text-blue-900">Technical Expertise in Paradise:</strong> Our specialized facility is equipped with the advanced tools and technology needed to properly service sophisticated German & other import cars from throughout North Texas. Many customers find our expertise worth the drive to Paradise.
            </p>
          </div>
        </div>
      </section>

      {/* Dallas-Specific SEO Content */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center text-gray-900">European Auto Repair Dallas - Your Trusted European Car Specialists</h2>
            
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>European auto repair Dallas</strong> drivers trust: Paradise-Performance is the premier destination for European car service in the Dallas-Fort Worth metroplex. 
                Located strategically just 30 minutes north of Dallas, we provide dealership-quality <strong>European auto repair</strong> at independent shop prices. 
                Our expert technicians specialize in all major European brands, delivering the precision and expertise your import vehicle deserves.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">European Auto Repair Dallas Services:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ BMW repair & service Dallas</li>
                    <li>✓ Mercedes-Benz repair Dallas</li>
                    <li>✓ Porsche service Dallas</li>
                    <li>✓ Audi repair Dallas TX</li>
                    <li>✓ Land Rover & Jaguar service</li>
                    <li>✓ Volkswagen & MINI repair</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">Why Dallas Chooses Us:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ 30-40% less than Dallas dealerships</li>
                    <li>✓ Same-day diagnostics available</li>
                    <li>✓ OEM parts & equipment</li>
                    <li>✓ 12-month/12,000-mile warranty</li>
                    <li>✓ Expert European technicians</li>
                    <li>✓ All European makes & models</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <h3 className="text-3xl font-bold mt-8 mb-6">We're Not Far from Dallas</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Yeah, we're in Paradise, not Dallas proper. But we're only 40 minutes from Highland Park, University Park, North Dallas, Preston Hollow, 
              Uptown, Oak Lawn, Richardson, Plano, Frisco, McKinney, Allen—basically anywhere in DFW. 
              Most of our customers say the drive is worth it once they see what the dealerships were charging them.
            </p>
            
            <h3 className="text-3xl font-bold mt-8 mb-6">Why Dallas Folks Drive Out Here</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Dallas has more BMWs, Mercedes, and Porsches than just about anywhere in Texas. Problem is, most shops either don't know how to work on them properly, or they charge dealership prices. 
              We've got the same diagnostic equipment the dealerships have—ISTA for BMWs, STAR for Mercedes, PIWIS for Porsches, all that stuff. But we don't have a fancy showroom to pay for, so our prices are way better. 
              Need <Link to="/services/european-car-repair/bmw" className="text-blue-600 hover:text-blue-800 underline">BMW repair in Dallas</Link>? We do those all day. 
              Looking for <Link to="/services/european-car-repair/porsche" className="text-blue-600 hover:text-blue-800 underline">Porsche repair Dallas</Link> specialists? We're IMS bearing experts. 
              Want quality <Link to="/services/european-car-repair/audi" className="text-blue-600 hover:text-blue-800 underline">Audi repair in Dallas</Link>? Quattro and DSG are our specialty.
            </p>
            
            <h3 className="text-2xl font-bold mt-8 mb-4">Getting Here from Dallas</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Take US-380 West from Dallas. About 30 minutes, straight shot. We're at 305 Co Rd 3470 in Paradise, TX 76073. 
              Drop your car off, grab a loaner or use our shuttle, and we'll call you when it's done. A lot of our Dallas customers just work from home that day—it's easier than you'd think.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg mt-8">
              <h3 className="text-xl font-bold mb-3">Dallas Area European Car Service Statistics</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary-600 font-bold mr-2">•</span>
                  <span>30-40% savings compared to Dallas dealership prices</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 font-bold mr-2">•</span>
                  <span>Same-day diagnostics for most European vehicles</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 font-bold mr-2">•</span>
                  <span>12-month/12,000-mile warranty on all repairs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 font-bold mr-2">•</span>
                  <span>We work on European cars all day, every day</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 font-bold mr-2">•</span>
                  <span>OEM parts and factory-level diagnostic equipment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            European Auto Repair for Dallas
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Real mechanics • Right parts • Way cheaper than dealerships
          </p>
          
          <div className="bg-white/10 backdrop-blur rounded-lg p-8 max-w-3xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <a
                href="tel:8173822316"
                className="inline-flex items-center justify-center px-6 py-4 bg-white text-primary-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                <FiPhone className="mr-2" />
                (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                Schedule Service
              </Link>
            </div>
            
            <div>
              <p className="flex items-center justify-center mb-2">
                <FiMapPin className="mr-2" />
                305 Co Rd 3470, Paradise, TX 76073
              </p>
              <p className="text-sm">
                30 min from Dallas • 35 min from Richardson • 25 min from Denton
              </p>
              <p className="text-sm mt-2">
                <FiClock className="inline mr-2" />
                Mon-Fri 9AM-5PM • Sat 10AM-2PM (Sales Only)
              </p>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default EuropeanCarRepair;
